import type {Integer, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {DateTime} from "luxon";
import {TuffexProductCatalogueRowStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/tuffexProductCatalogueWorkspaceCapability/domain/TuffexProductCatalogueRow";
import type {Customer} from "~/domainModels/ottokart/customers";
import type {AdvancedQuotation, Quotation} from "~/domainModels/ottokart/quotations";
import {HoverCard, Modal} from "@mantine/core";
import {useState} from "react";
import React from "react";
import {SimpleTag as Tag} from "@atlaskit/tag";
import {DiscountType, quotationStatusMap, QUOTATION_EXPIRING_SOON_BEFORE_DAYS, QUOTATION_VALIDITY_DAYS} from "~/utilities/ottokart/quotationConstants";

/**
 * Used to tag a string so that we can automatically detect uniqueness across the codebase later.
 */
export function uniqueString(strings: TemplateStringsArray): string {
    return strings.join();
}

/**
 * Used to tag a string so that we can automatically detect uniqueness across the codebase later.
 */
export function uniqueUuid(strings: TemplateStringsArray): Uuid {
    return strings.join() as Uuid;
}

/**
 * @param min Inclusive
 * @param max Exclusive
 */
export function getRandomInteger(min: Integer, max: Integer): Integer {
    const range = max - min;
    return (min + Math.floor(Math.random() * range)) as Integer;
}

/**
 * @param min Inclusive
 * @param max Exclusive
 */
export function getRandomFloat(min: Integer, max: Integer): number {
    const nonFractionalPart = getRandomInteger(min, max);
    const fractionalPart = getRandomInteger(0 as Integer, 100 as Integer);
    return nonFractionalPart + fractionalPart / 100;
}

export function getRandomImageUrl(): string {
    return `https://picsum.photos/id/${getRandomInteger(0 as Integer, 128 as Integer)}/160/90`;
}

/**
 * Sample output: 1st Jan 2025
 */
export function formatDate(isoString: string): string {
    const dt = DateTime.fromISO(isoString, {zone: "Asia/Kolkata"});

    const day = getOrdinal(dt.day); // Ordinal (e.g. 1st)
    const month = dt.toFormat("LLL"); // Short month name (e.g. Oct)
    const year = dt.toFormat("yyyy");

    return `${day} ${month} ${year}`;
}

/**
 * Sample output: 10:30 AM, 1st Jan 2025, IST
 */
export function formatDateTime(isoString: string): string {
    const dt = DateTime.fromISO(isoString, {zone: "Asia/Kolkata"});

    const day = dt.day; // Ordinal (e.g. 1st)
    const time = dt.toFormat("hh:mm a"); // 12-hour format with AM/PM
    const month = dt.toFormat("LLL"); // Short month name (e.g. Oct)
    const year = dt.toFormat("yyyy");

    return `${day} ${month} ${year}, ${time}`;
}

export function formatMediaUploadedAt(isoString: string | undefined | null): string {
    if (isoString === undefined || isoString === null) {
        return "—";
    }

    const raw = isoString.trim();
    if (raw.length === 0) {
        return "—";
    }

    const parseAttempts: Array<() => DateTime> = [
        () => DateTime.fromISO(raw, {zone: "Asia/Kolkata"}),
        () => DateTime.fromISO(raw, {setZone: true}),
        () => DateTime.fromSQL(raw, {zone: "Asia/Kolkata"}),
    ];

    const asNumber = Number(raw);
    if (!Number.isNaN(asNumber)) {
        parseAttempts.push(() => DateTime.fromMillis(asNumber, {zone: "Asia/Kolkata"}));
    }

    for (const attempt of parseAttempts) {
        const dt = attempt();
        if (dt.isValid) {
            const day = dt.day;
            const time = dt.toFormat("hh:mm a");
            const month = dt.toFormat("LLL");
            const year = dt.toFormat("yyyy");
            return `${day} ${month} ${year}, ${time}`;
        }
    }

    return "—";
}


export function formatDateAndTime(isoString: string): {date: string; time: string} {
    const dt = DateTime.fromISO(isoString, {zone: "Asia/Kolkata"});

    const day = getOrdinal(dt.day); // Ordinal (e.g. 1st)
    const time = dt.toFormat("hh:mm a"); // 12-hour format with AM/PM
    const month = dt.toFormat("LLL"); // Short month name (e.g. Oct)
    const year = dt.toFormat("yyyy");

    return {date: `${day} ${month} ${year}`, time: time};
}

function getOrdinal(day: number): string {
    const suffix = day % 10 === 1 && day !== 11 ? "st" : day % 10 === 2 && day !== 12 ? "nd" : day % 10 === 3 && day !== 13 ? "rd" : "th";
    return `${day}${suffix}`;
}

/**
 * Takes something like `/products` as input, and creates a regex that matches
 * the exact path, optionally a trailing slash and optionally any search params
 */
export function getUrlRegexForPathName(pathName: string): RegExp {
    // eg: `/\/p\/authentication(\?.*)?$/`
    return new RegExp(`^${pathName}(\\?.*)?$`);
}

export function getInitials(name: string): string {
    if (!name) {
        return "";
    }
    return name
        .split(" ")
        .filter((part) => part.length > 0)
        .map((part) => part[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
}

export function generateInitialsAvatarDataUrl(name: string): string {
    const initials = getInitials(name);
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const h = Math.abs(hash) % 360;
    const color = `hsl(${h}, 65%, 45%)`;

    const svg = `
<svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
  <rect width="100" height="100" fill="${color}"/>
  <text x="50" y="50" dominant-baseline="central" text-anchor="middle" font-family="Inter, sans-serif" font-size="40" font-weight="600" fill="white">${initials}</text>
</svg>
    `.trim();

    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

const QUOTATION_STATUS_REQUESTED = "requested";
const QUOTATION_STATUS_EXPIRING_SOON = "expiring-soon";
const QUOTATION_STATUS_EXPIRED = "expired";

export type QuotationStatusUi = {
    label: "Requested" | "Sent" | "Expiring Soon" | "Expired";
    className: string;
};

function resolveQuotationStatusByValidity(
    status: string,
    issueDateIsoString?: string,
): string {
    const normalized = status.trim().toLowerCase();
    const mappedStatus = quotationStatusMap[status] ?? quotationStatusMap[normalized] ?? normalized;

    if (issueDateIsoString === undefined || issueDateIsoString.trim().length === 0) {
        return mappedStatus;
    }

    const issueDate = DateTime.fromISO(issueDateIsoString, {zone: "Asia/Kolkata"}).startOf("day");
    if (!issueDate.isValid) {
        return mappedStatus;
    }

    const currentDate = DateTime.now().setZone("Asia/Kolkata").startOf("day");
    const validityEndDate = issueDate.plus({days: QUOTATION_VALIDITY_DAYS});
    const expiringSoonDate = validityEndDate.minus({days: QUOTATION_EXPIRING_SOON_BEFORE_DAYS});

    if (currentDate >= validityEndDate) {
        return QUOTATION_STATUS_EXPIRED;
    }
    if (currentDate >= expiringSoonDate) {
        return QUOTATION_STATUS_EXPIRING_SOON;
    }

    return mappedStatus;
}

/** Display order for quotation status values in filter dropdowns. */
export const QUOTATION_STATUS_FILTER_DISPLAY_ORDER = ["Sent", "Requested", "Expiring Soon", "Expired"] as const;

export function sortQuotationStatusFilterValues(values: ReadonlyArray<string>): Array<string> {
    const orderIndex = new Map(QUOTATION_STATUS_FILTER_DISPLAY_ORDER.map((label, index) => [label, index] as const));

    return [...values].sort((left, right) => {
        const leftIndex = orderIndex.get(left);
        const rightIndex = orderIndex.get(right);
        if (leftIndex !== undefined && rightIndex !== undefined) {
            return leftIndex - rightIndex;
        }
        if (leftIndex !== undefined) {
            return -1;
        }
        if (rightIndex !== undefined) {
            return 1;
        }
        return left.localeCompare(right);
    });
}

/** Maps display labels used in filters/tables to the same badge styles as {@link getQuotationStatusUi}. */
export function getQuotationStatusUiByDisplayLabel(label: string): QuotationStatusUi {
    const normalized = label.trim().toLowerCase();
    if (normalized === "sent") {
        return {
            label: "Sent",
            className: "bg-[#efffd6] text-[#637e3b] border border-[#637e3b]",
        };
    }
    if (normalized === "expiring soon") {
        return {
            label: "Expiring Soon",
            className: "bg-[#f8eefe] text-[#803fa5] border border-[#803fa5]",
        };
    }
    if (normalized === "expired") {
        return {
            label: "Expired",
            className: "bg-[#ffeceb] text-[#ba4d45] border border-[#ba4d45]",
        };
    }
    if (normalized === "requested") {
        return {
            label: "Requested",
            className: "bg-[#fff5db] text-[#e06c00] border border-[#e06c00]",
        };
    }
    return {
        label: "Requested",
        className: "bg-[#fff5db] text-[#e06c00] border border-[#e06c00]",
    };
}

type QuotationVersionIdentity = {
    id: string;
    updatedAt: string;
};

/** Latest revision in a quotation family — same rule as the quotations list `latestQuotationId`. */
export function resolveQuotationDefaultVersionId(versions: Array<QuotationVersionIdentity> | undefined, fallbackId: string): string {
    if (versions === undefined || versions.length === 0) {
        return fallbackId;
    }

    return versions.reduce((latest, current) => (new Date(current.updatedAt).getTime() > new Date(latest.updatedAt).getTime() ? current : latest)).id;
}

export function QuotationVersionDefaultBadge() {
    return (
        <span className="inline-flex items-center rounded border border-[#0C66E4] px-1.5 py-px text-[11px] font-semibold leading-4 text-[#0C66E4]">
            Default
        </span>
    );
}

export function getQuotationStatusUi(
    status: string,
    issueDateIsoString?: string,
    _validUntilIso?: string | null,
): QuotationStatusUi {
    const normalized = resolveQuotationStatusByValidity(status, issueDateIsoString);

    if (normalized.includes("sent")) {
        return {
            label: "Sent",
            className: "bg-[#efffd6] text-[#637e3b] border border-[#637e3b]",
        };
    }

    if (normalized.includes("expiring")) {
        return {
            label: "Expiring Soon",
            className: "bg-[#f8eefe] text-[#803fa5] border border-[#803fa5]",
        };
    }

    if (normalized.includes("expired")) {
        return {
            label: "Expired",
            className: "bg-[#ffeceb] text-[#ba4d45] border border-[#ba4d45]",
        };
    }

    if (normalized.includes("requested")) {
        return {
            label: "Requested",
            className: "bg-[#fff5db] text-[#e06c00] border border-[#e06c00]",
        };
    }

    return {
        label: "Requested",
        className: "bg-[#fff5db] text-[#e06c00] border border-[#e06c00]",
    };
}

export type CollectionStatusUi = {
    label: "Active" | "Draft";
    className: string;
};

export function getCollectionStatusUi(isPublished: boolean): CollectionStatusUi {
    if (isPublished) {
        return {
            label: "Active",
            className: "bg-[#efffd6] text-[#637e3b] border border-[#637e3b]",
        };
    }

    return {
        label: "Draft",
        className: "bg-[#fff5db] text-[#e06c00] border border-[#e06c00]",
    };
}

export type ProductStatusUi = {
    label: "Active" | "Draft" | "Under review";
    className: string;
};

export function getProductStatusUi(isPublic: boolean): ProductStatusUi {
    if (isPublic === true) {
        return {
            label: "Active",
            className: "bg-[#efffd6] text-[#637e3b] border border-[#637e3b]",
        };
    }

    return {
        label: "Draft",
        className: "bg-[#F0F1F2] text-[#8C8F97] border border-[#8C8F97]",
    };
}

export function getCatalogueProductStatusUi(status: TuffexProductCatalogueRowStatus): ProductStatusUi {
    if (status === TuffexProductCatalogueRowStatus.active) {
        return {
            label: "Active",
            className: "bg-[#efffd6] text-[#637e3b] border border-[#637e3b]",
        };
    }
    if (status === TuffexProductCatalogueRowStatus.underReview) {
        return {
            label: "Under review",
            className: "bg-[#fff5db] text-[#e06c00] border border-[#e06c00]",
        };
    }
    return {
        label: "Draft",
        className: "bg-[#F0F1F2] text-[#8C8F97] border border-[#8C8F97]",
    };
}

export function getCustomerDisplayName(customer: Customer | undefined): string {
    if (!customer) {
        return "-";
    }

    const firstName = customer.firstName?.trim() ?? "";
    const lastName = customer.lastName?.trim() ?? "";
    const fullName = `${firstName} ${lastName}`.trim();

    if (fullName.length > 0) {
        return fullName;
    }
    return customer.email ?? customer.phoneNumber;
}

export function formatQuotationAddress(quotation: Quotation | undefined): string {
    if (!quotation) {
        return "-";
    }

    const address = quotation.billingDetailsAddress?.trim() ?? "";
    const pinCode = quotation.billingDetailsPinCode?.trim() ?? "";
    const city = quotation.billingDetailsCity?.trim() ?? "";
    const state = quotation.billingDetailsState?.trim() ?? "";

    const lineOne = address.length > 0 ? address : "-";
    const lineTwo = [pinCode, city, state].filter((value) => value.length > 0).join(", ");

    return lineTwo.length > 0 ? `${lineOne}, ${lineTwo}` : lineOne;
}

export function calculateQuotationTotal(quotation: Quotation | AdvancedQuotation | undefined): number {
    if (!quotation) {
        return 0;
    }

    const subtotal = quotation.items.reduce((sum, item) => {
        const price = Number(
            (item as {sellingPrice?: number; pricePerUnit?: number}).sellingPrice ?? (item as {sellingPrice?: number; pricePerUnit?: number}).pricePerUnit ?? 0,
        );
        const quantity = Number((item as {quantity?: number}).quantity ?? 0);
        return sum + price * quantity;
    }, 0);
    const freightCharges = Number(quotation.freightCharges ?? 0);
    const totalLineItemDiscount = quotation.items.reduce((sum, item) => {
        const price = Number(
            (item as {sellingPrice?: number; pricePerUnit?: number}).sellingPrice ?? (item as {sellingPrice?: number; pricePerUnit?: number}).pricePerUnit ?? 0,
        );
        const quantity = Number((item as {quantity?: number}).quantity ?? 0);
        const discountPercent = Number((item as {discount?: number | null}).discount ?? 0);
        if (discountPercent <= 0) {
            return sum;
        }
        return sum + quantity * (price * (discountPercent / 100));
    }, 0);
    const discountedSubtotal = Math.max(subtotal - totalLineItemDiscount, 0);
    const taxAmount = discountedSubtotal * ((quotation.taxRate ?? 0) / 100);
    const totalBeforeRoundOff = discountedSubtotal + taxAmount + freightCharges;
    const totalBeforeRoundOffPaise = Math.round(totalBeforeRoundOff * 100);
    const roundedTotalPaise = Math.ceil(totalBeforeRoundOffPaise / 100) * 100;

    return roundedTotalPaise / 100;
}

export function calculateQuotationBreakdown(quotation: Quotation | undefined): {
    subtotal: number;
    discountAmount: number;
    discountedSubtotal: number;
    freightCharges: number;
    taxRate: number;
    taxAmount: number;
    total: number;
} {
    if (!quotation) {
        return {
            subtotal: 0,
            discountAmount: 0,
            discountedSubtotal: 0,
            freightCharges: 0,
            taxRate: 0,
            taxAmount: 0,
            total: 0,
        };
    }

    const subtotal = quotation.items.reduce((sum, item) => {
        const price = Number(
            (item as {sellingPrice?: number; pricePerUnit?: number}).sellingPrice ?? (item as {sellingPrice?: number; pricePerUnit?: number}).pricePerUnit ?? 0,
        );
        const quantity = Number((item as {quantity?: number}).quantity ?? 0);
        return sum + price * quantity;
    }, 0);
    const discountValue = quotation.discountValue ?? 0;
    const discountAmount =
        quotation.discountType === DiscountType.PERCENTAGE
            ? subtotal * (discountValue / 100)
            : quotation.discountType === DiscountType.FLAT
              ? discountValue
              : 0;
    const discountedSubtotal = Math.max(subtotal - discountAmount, 0);
    const freightCharges = Number(quotation.freightCharges ?? 0);
    const taxRate = quotation.taxRate ?? 0;
    const taxAmount = discountedSubtotal * (taxRate / 100);
    const total = discountedSubtotal + taxAmount + freightCharges;

    return {
        subtotal,
        discountAmount,
        discountedSubtotal,
        freightCharges,
        taxRate,
        taxAmount,
        total,
    };
}

export function formatCurrency(value: number): string {
    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
    }).format(value ?? 0);
}

function useIsMobile() {
    const [isMobile, setIsMobile] = React.useState(false);

    React.useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 768);
        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, []);

    return isMobile;
}

export function QuotationSkusCell({skus}: {skus: string[]}) {
    const [isSkuModalOpen, setIsSkuModalOpen] = useState(false);
    const isMobile = useIsMobile();
    const firstSku = skus[0];
    const extraSkus = skus.slice(1);
    const remainingCount = extraSkus.length;

    const skuExtraTagClassName = "text-xs font-medium px-2.5 py-1 bg-white text-[#172B4D] rounded max-w-[300px] break-all";

    const extraSkusPanel = (
        <>
            <p className="text-[14px] text-white/80 text-left">Additional SKUs: {remainingCount}</p>
            <div className="mt-2 flex flex-wrap justify-start gap-1.5">
                {extraSkus.map((sku, index) => (
                    <span
                        key={`${sku}-extra-${index}`}
                        className={skuExtraTagClassName}
                    >
                        {sku}
                    </span>
                ))}
            </div>
        </>
    );

    const skuExtrasModalStyles = {
        content: {
            backgroundColor: "#000000",
            color: "#FFFFFF",
            width: "fit-content",
            maxWidth: "min(420px, 90vw)",
        },
        header: {backgroundColor: "#000000", justifyContent: "flex-start"},
        body: {backgroundColor: "#000000"},
        title: {color: "#FFFFFF", fontWeight: 600, textAlign: "left", flex: 1},
        close: {color: "#FFFFFF"},
    } as const;

    if (firstSku === undefined) {
        return <span>-</span>;
    }

    if (remainingCount === 0) {
        return (
            <div className="flex items-center justify-start gap-2 max-w-full h-full">
                <Tag
                    text={firstSku}
                    appearance="default"
                />
            </div>
        );
    }

    const remainingCountTag = (
        <Tag
            text={`+${remainingCount}`}
            appearance="default"
        />
    );

    if (isMobile) {
        return (
            <>
                <div className="flex items-center justify-start gap-2 max-w-full h-full cursor-pointer [&_*]:cursor-pointer">
                    <Tag
                        text={firstSku}
                        appearance="default"
                    />
                    <button
                        type="button"
                        data-quotation-skus="true"
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            setIsSkuModalOpen(true);
                        }}
                        className="inline-flex cursor-pointer [&_*]:cursor-pointer"
                    >
                        {remainingCountTag}
                    </button>
                </div>

                <Modal
                    opened={isSkuModalOpen}
                    onClose={() => setIsSkuModalOpen(false)}
                    title={`Additional SKUs: ${remainingCount}`}
                    centered
                    styles={skuExtrasModalStyles}
                >
                    <div className="flex flex-wrap justify-start gap-1.5">
                        {extraSkus.map((sku, index) => (
                            <span
                                key={`${sku}-modal-${index}`}
                                className={skuExtraTagClassName}
                            >
                                {sku}
                            </span>
                        ))}
                    </div>
                </Modal>
            </>
        );
    }

    return (
        <div className="flex items-center justify-start gap-2 max-w-full h-full">
            <Tag
                text={firstSku}
                appearance="default"
            />
            <HoverCard
                openDelay={80}
                closeDelay={100}
                shadow="md"
                withArrow
                styles={{
                    dropdown: {
                        backgroundColor: "#000000",
                        border: "none",
                        width: "max-content",
                        maxWidth: "min(420px, 90vw)",
                    },
                    arrow: {
                        backgroundColor: "#000000",
                        border: "none",
                    },
                }}
            >
                <HoverCard.Target>
                    <span
                        data-quotation-skus="true"
                        className="inline-flex"
                    >
                        {remainingCountTag}
                    </span>
                </HoverCard.Target>

                <HoverCard.Dropdown>{extraSkusPanel}</HoverCard.Dropdown>
            </HoverCard>
        </div>
    );
}

export function QuotationCompatibleMachinesCell({compatibleMachines}: {compatibleMachines: string[]}) {
    if (compatibleMachines.length === 0) {
        return <span>—</span>;
    }

    const [isModalOpen, setIsModalOpen] = useState(false);
    const isMobile = useIsMobile();
    const visibleMachines = compatibleMachines.slice(0, 1);
    const remainingCount = compatibleMachines.length - visibleMachines.length;
    const shouldShowHover = compatibleMachines.length > 1;

    const summary = (
        <div className="flex items-center justify-start gap-2 max-w-full h-full">
            {visibleMachines.map((machine, index) => (
                <Tag
                    key={`${machine}-${index}`}
                    text={machine}
                    appearance="default"
                />
            ))}
            {remainingCount > 0 ? (
                <Tag
                    key={`remaining-compatible-${remainingCount}`}
                    text={`+${remainingCount}`}
                    appearance="default"
                />
            ) : null}
        </div>
    );

    if (!shouldShowHover) {
        return summary;
    }

    if (isMobile) {
        return (
            <>
                <button
                    type="button"
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setIsModalOpen(true);
                    }}
                    className="w-full text-left"
                >
                    {summary}
                </button>

                <Modal
                    opened={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title={`Total compatible machines: ${compatibleMachines.length}`}
                    centered
                >
                    <div className="flex flex-wrap justify-start gap-1.5">
                        {compatibleMachines.map((machine, index) => (
                            <Tag
                                key={`${machine}-modal-${index}`}
                                text={machine}
                                appearance="default"
                            />
                        ))}
                    </div>
                </Modal>
            </>
        );
    }

    return (
        <HoverCard
            openDelay={80}
            closeDelay={100}
            shadow="md"
            withArrow
            width={420}
        >
            <HoverCard.Target>{summary}</HoverCard.Target>
            <HoverCard.Dropdown>
                <p className="text-[11px] text-gray-500 text-center">Total compatible machines selected: {compatibleMachines.length}</p>
                <div className="mt-2 flex flex-wrap justify-start gap-1.5">
                    {compatibleMachines.map((machine, index) => (
                        <span
                            key={`${machine}-hover-${index}`}
                            className="text-xs px-2.5 py-1 bg-gray-100 rounded max-w-[300px] break-all"
                        >
                            {machine}
                        </span>
                    ))}
                </div>
            </HoverCard.Dropdown>
        </HoverCard>
    );
}

export function Info({label, value}: {label: string; value: string}) {
    return (
        <div className="flex flex-col gap-ds-space-100">
            <p className="text-sm font-medium text-gray-600">{label}</p>
            <div className="text-sm bg-gray-50 rounded-lg px-ds-space-200 py-ds-space-150 min-h-[2.5rem] flex items-center">{value}</div>
        </div>
    );
}

export function getQuotationStatusLabel(status: string): string {
    const normalized = resolveQuotationStatusByValidity(status);

    if (normalized.includes("viewed") || normalized.includes("seen")) {
        return "Viewed";
    }
    if (normalized.includes("sent") || normalized === "quotation sent") {
        return "Sent";
    }
    if (normalized === "expired") {
        return "Expired";
    }
    if (normalized === QUOTATION_STATUS_REQUESTED || normalized === "requested") {
        return "Requested";
    }
    return "Requested";
}

export function fallback<T>(value: T | undefined | null, fallbackValue: T): T;
export function fallback(value: string | undefined | null): string;
export function fallback<T>(value: T | undefined | null, fallbackValue?: T): T | string {
    if (value === undefined || value === null) {
        return fallbackValue !== undefined ? fallbackValue : "-";
    }

    if (typeof value === "string" && value.trim().length === 0) {
        return fallbackValue !== undefined ? fallbackValue : "-";
    }

    return value;
}

export const DEFAULT_TERMS_AND_CONDITIONS = [
    "GST: Extra at actual time of billing. At present 18%.",
    "Delivery: EX-stock Available, Subject Prior Sale",
    "Payment: 100% against Proforma Invoice.",
    "Price Basis: EX Works, IIMS Hyderabad.",
    "Validity: This quotation is valid for a period of ten (10) days from the date of issue.",
    "Warranty: Applicable only on Full undercarriage set.",
    "IIMS is a registered MSME under the MSMED Act, 2006.",
];

export const COMPANY_DETAILS = {
    title: "IIMS-Jeedimetla Plant",
    line1: "IDA JEEDIMETLA, PHASE -1, Medchal, Malkajgiri, JEEDIMETLA,",
    line2: "QUTHBULLAPUR, SURVEY NO.254,255 & 256, Hyderabad,",
    line3: "500055, INDIA",
    stateCode: "36 - TELANGANA",
    gstin: "36AAHCI1010J1ZX",
    cin: "U45101WB2023PLC261550",
    udyamRegNo: "UDYAM-TS-09-0254248",
    currency: "INR",
} as const;

export const QUOTATION_SAVE_SUCCESS_FLASH_KEY = "quotation-save-success-flash";

export const QUOTATION_SAVE_SUCCESS_MESSAGE = {
    saved: "Quotation was successfully saved",
    savedAndShared: "Quotation was successfully saved and shared with customer",
} as const;

/** Shown on the mobile quotation editor after saving a sub-panel (details, order summary, T&C). */
export const MOBILE_QUOTATION_EDITOR_CHANGES_SAVED_MESSAGE = "Changes saved";

export type QuotationSaveSuccessFlashVariant = keyof typeof QUOTATION_SAVE_SUCCESS_MESSAGE;

export const QUOTATION_SAVE_SUCCESS_ROUTER_STATE_KEY = "quotationSaveSuccessFlash";

export function setQuotationSaveSuccessFlash(variant: QuotationSaveSuccessFlashVariant) {
    if (typeof window === "undefined") {
        return;
    }
    sessionStorage.setItem(QUOTATION_SAVE_SUCCESS_FLASH_KEY, QUOTATION_SAVE_SUCCESS_MESSAGE[variant]);
}

export function getQuotationSaveSuccessFlashFromRouterState(state: unknown): string | null {
    if (state === null || typeof state !== "object") {
        return null;
    }
    const message = (state as Record<string, unknown>)[QUOTATION_SAVE_SUCCESS_ROUTER_STATE_KEY];
    return typeof message === "string" && message.length > 0 ? message : null;
}

export function peekQuotationSaveSuccessFlash(): string | null {
    if (typeof window === "undefined") {
        return null;
    }
    return sessionStorage.getItem(QUOTATION_SAVE_SUCCESS_FLASH_KEY);
}

export function clearQuotationSaveSuccessFlash() {
    if (typeof window === "undefined") {
        return;
    }
    sessionStorage.removeItem(QUOTATION_SAVE_SUCCESS_FLASH_KEY);
}

/** @deprecated Prefer peek on mount and clear on dismiss (see useQuotationSaveSuccessToast). */
export function consumeQuotationSaveSuccessFlash(): string | null {
    const message = peekQuotationSaveSuccessFlash();
    if (message !== null) {
        clearQuotationSaveSuccessFlash();
    }
    return message;
}

// ----------------------------
// Quotation editor (create/revise/duplicate) shared helpers
// ----------------------------

export type QuotationEditorFormState = {
    contactNumber: string;
    emailId: string;
    incoTerms: string;
    termsOfPayment: string;
    customerReferenceNumber: string;
    customerReferenceDate: string;
    consigneeShipTo: string;
    shippingAddress: string;
    buyerBillTo: string;
    billingAddress: string;
    freightCharges: string;
    isFreightEnabled: boolean;
};

export type QuotationEditorTermsRow = {
    id: string;
    value: string;
    isVisible: boolean;
    canDelete: boolean;
};

export type QuotationEditorOrderItemRow = {
    id: string;
    variantId?: string;
    productId?: string;
    productName: string;
    categoryName: string;
    sku: string;
    inventory: number;
    quantity: string;
    price: number;
    discountPercent: string;
    discountValue: number;
    total: number;
    isCustom?: boolean;
    sortOrder?: number;
    compatibleMachines: string[];
    lineDiscountAmountOverride?: string;
    lineTaxableAmountOverride?: string;
};

export type QuotationEditorAddressKind = "shipping" | "billing";

export type QuotationEditorAddressDraft = {
    companyName: string;
    gstin: string;
    address: string;
    city: string;
    state: string;
    pinCode: string;
    email: string;
    phoneNumber: string;
};

export type QuotationEditorQuotationFieldErrors = Partial<
    Record<
        keyof Pick<
            QuotationEditorFormState,
            | "contactNumber"
            | "emailId"
            | "incoTerms"
            | "termsOfPayment"
            | "customerReferenceNumber"
            | "customerReferenceDate"
            | "consigneeShipTo"
            | "buyerBillTo"
        >,
        string
    >
>;

export type QuotationEditorAddressFieldErrors = Partial<Record<keyof QuotationEditorAddressDraft, string>>;

export function asTrimmedString(value: unknown): string {
    if (typeof value === "string") {
        return value.trim();
    }
    if (value === null || value === undefined) {
        return "";
    }
    return String(value).trim();
}

export const QUOTATION_INCO_TERMS_OPTIONS = ["Delivered at Place", "Ex Works"] as const;

export const QUOTATION_TERMS_OF_PAYMENT_OPTIONS = ["Immediate/Advance Payment", "Within 15 days due net", "Within 30 days due net"] as const;

export type QuotationFixedSelectOption = {
    label: string;
    value: string;
};

export const QUOTATION_INCO_TERMS_SELECT_OPTIONS: Array<QuotationFixedSelectOption> = QUOTATION_INCO_TERMS_OPTIONS.map((option) => ({
    label: option,
    value: option,
}));

export const QUOTATION_TERMS_OF_PAYMENT_SELECT_OPTIONS: Array<QuotationFixedSelectOption> = QUOTATION_TERMS_OF_PAYMENT_OPTIONS.map((option) => ({
    label: option,
    value: option,
}));

const REVISE_QUOTATION_CLOSE_TARGET_KEY_PREFIX = "revise-quotation-close-target:";

export type ReviseQuotationCloseTarget = "view-quotation" | "quotations-list";

const VIEW_QUOTATION_CLOSE_TARGET_KEY_PREFIX = "view-quotation-close-target:";
const VIEW_QUOTATION_ENTRY_VERSION_KEY_PREFIX = "view-quotation-entry-version:";

export type ViewQuotationCloseTarget = "quotations-list" | "quotation-versions";

/** Set when navigating to view quotation so close (X) returns to the correct screen. */
export function rememberViewQuotationCloseTarget(quotationId: string, target: ViewQuotationCloseTarget, sourceQuotationId?: string): void {
    if (typeof sessionStorage === "undefined") {
        return;
    }
    sessionStorage.setItem(`${VIEW_QUOTATION_CLOSE_TARGET_KEY_PREFIX}${quotationId}`, target);
    if (sourceQuotationId !== undefined) {
        sessionStorage.setItem(`${VIEW_QUOTATION_ENTRY_VERSION_KEY_PREFIX}${sourceQuotationId}`, quotationId);
    }
}

export function getViewQuotationCloseTarget(quotationId: string): ViewQuotationCloseTarget | null {
    if (typeof sessionStorage === "undefined") {
        return null;
    }
    const raw = sessionStorage.getItem(`${VIEW_QUOTATION_CLOSE_TARGET_KEY_PREFIX}${quotationId}`);
    if (raw === "quotations-list" || raw === "quotation-versions") {
        return raw;
    }
    return null;
}

export function takeViewQuotationCloseTarget(quotationId: string): ViewQuotationCloseTarget | null {
    if (typeof sessionStorage === "undefined") {
        return null;
    }
    const key = `${VIEW_QUOTATION_CLOSE_TARGET_KEY_PREFIX}${quotationId}`;
    const raw = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
    if (raw === "quotations-list" || raw === "quotation-versions") {
        return raw;
    }
    return null;
}

export function getViewQuotationEntryVersion(sourceQuotationId: string): string | null {
    if (typeof sessionStorage === "undefined") {
        return null;
    }
    return sessionStorage.getItem(`${VIEW_QUOTATION_ENTRY_VERSION_KEY_PREFIX}${sourceQuotationId}`);
}

export function clearViewQuotationEntryVersion(sourceQuotationId: string): void {
    if (typeof sessionStorage === "undefined") {
        return;
    }
    sessionStorage.removeItem(`${VIEW_QUOTATION_ENTRY_VERSION_KEY_PREFIX}${sourceQuotationId}`);
}

/**
 * Default badge target in view quotation — the table row slug used when opening view:
 * main quotations list stores the latest revision; version list stores the clicked row.
 */
export function resolveViewQuotationDefaultVersionId(versions: Array<QuotationVersionIdentity> | undefined, sourceQuotationId: string): string {
    const latestVersionId = resolveQuotationDefaultVersionId(versions, sourceQuotationId);
    const entryVersionId = getViewQuotationEntryVersion(sourceQuotationId);
    if (entryVersionId !== null && versions?.some((version) => version.id === entryVersionId)) {
        return entryVersionId;
    }

    return latestVersionId;
}

export function getQuotationVersionDefaultStatusUi(): QuotationStatusUi {
    return {
        label: "Default",
        className: "border border-[#0C66E4] text-[#0C66E4]",
    };
}

/** Set when navigating to revise so close (X) goes to the correct screen, not browser history. */
export function rememberReviseQuotationCloseTarget(quotationId: string, target: ReviseQuotationCloseTarget): void {
    if (typeof sessionStorage === "undefined") {
        return;
    }
    sessionStorage.setItem(`${REVISE_QUOTATION_CLOSE_TARGET_KEY_PREFIX}${quotationId}`, target);
}

export function clearReviseQuotationCloseTarget(quotationId: string): void {
    if (typeof sessionStorage === "undefined") {
        return;
    }
    sessionStorage.removeItem(`${REVISE_QUOTATION_CLOSE_TARGET_KEY_PREFIX}${quotationId}`);
}

/** Read and remove the target stored for this quotation (invoke when closing revise). */
export function takeReviseQuotationCloseTarget(quotationId: string): ReviseQuotationCloseTarget | null {
    if (typeof sessionStorage === "undefined") {
        return null;
    }
    const key = `${REVISE_QUOTATION_CLOSE_TARGET_KEY_PREFIX}${quotationId}`;
    const raw = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
    if (raw === "view-quotation" || raw === "quotations-list") {
        return raw;
    }
    return null;
}

/**
 * Single value for Atlaskit/react-select: `null` when unset (no phantom first option).
 * When the stored string matches `canonicalSelectOptions`, returns **that exact object**
 * so `value` stays referentially identical to `options[*]` — required for react-select's
 * `openMenu()` to align keyboard focus with the selected row instead of defaulting to index 0.
 */
export function selectSingleValueFromFixedOptions(
    canonicalSelectOptions: ReadonlyArray<QuotationFixedSelectOption>,
    currentRaw: unknown,
): QuotationFixedSelectOption | null {
    const trimmed = asTrimmedString(currentRaw);
    if (trimmed.length === 0) {
        return null;
    }
    const match = canonicalSelectOptions.find((option) => option.value === trimmed);
    if (match !== undefined) {
        return match;
    }
    return {label: trimmed, value: trimmed};
}

/** Touch / mobile: Atlaskit options use `box-shadow: inset 2px 0 0` for focus; first row can stay "focused" and keep a blue bar. */
export const QUOTATION_FIXED_SELECT_STYLES = {
    option(provided: Record<string, unknown>, state: {isSelected: boolean}) {
        if (state.isSelected === true) {
            return provided;
        }
        const next: Record<string, unknown> = {...provided};
        next.backgroundColor = "transparent";
        next.backgroundImage = "none";
        next.boxShadow = "none";
        next.borderLeft = "none";
        next.borderInlineStart = "none";
        const active = next[":active"];
        if (active !== null && typeof active === "object") {
            next[":active"] = {
                ...(active as Record<string, unknown>),
                backgroundColor: "transparent",
                boxShadow: "none",
            };
        }
        return next;
    },
};

/**
 * Issuer company teaser for quotation mobile layouts (above "Quotation details").
 * Uses IIMS constants — not billing/shipping / customer-entered company.
 */
export function getQuotationIssuerCompanyMobilePreview(): {
    companyPreviewTitle: string;
    companyPreviewSubtitle: string;
} {
    const companyPreviewSubtitle = [COMPANY_DETAILS.line1, COMPANY_DETAILS.line2, COMPANY_DETAILS.line3]
        .map((line) => asTrimmedString(line))
        .filter((line) => line.length > 0)
        .join(" ");
    return {
        companyPreviewTitle: COMPANY_DETAILS.title,
        companyPreviewSubtitle,
    };
}

export function isPhoneNumberLike(value: string): boolean {
    const trimmed = value.trim();
    if (trimmed === "") {
        return false;
    }
    return /^[+\d][\d\s().-]*$/.test(trimmed);
}

/**
 * Prefer a human-entered name; if missing or phone-like, fall back to company name.
 */
export function resolvePartyName(primaryValue: string | null | undefined, companyNameFallback: string | null | undefined): string {
    const primary = asTrimmedString(primaryValue);
    if (primary.length > 0 && isPhoneNumberLike(primary) === false) {
        return primary;
    }
    return asTrimmedString(companyNameFallback);
}

export function formatAddressSummary(address: QuotationEditorAddressDraft): string {
    const line1 = address.companyName;
    const line2 = address.gstin ? `GST IN: ${address.gstin}` : "";
    const line3 = [address.address].filter((value) => value.length > 0).join(", ");
    const line4 = [address.city, address.state, address.pinCode].filter((value) => value.length > 0).join(", ");
    const line5 = [address.email, address.phoneNumber].filter((value) => value.length > 0).join(" | ");

    return [line1, line2, line3, line4, line5].filter((value) => value.length > 0).join("\n");
}

export function moveArrayItem<T>(items: Array<T>, fromIndex: number, toIndex: number): Array<T> {
    if (fromIndex === toIndex) {
        return items;
    }
    const next = [...items];
    const moved = next.splice(fromIndex, 1)[0];
    if (moved === undefined) {
        return items;
    }
    next.splice(toIndex, 0, moved);
    return next;
}

/**
 * Revise/Duplicate behavior: defaults are visible only if present in raw terms.
 */
export function mapTermsRowsReviseLike(rawTerms: Array<string> | null | undefined): Array<QuotationEditorTermsRow> {
    const terms = (rawTerms ?? []).map((term) => asTrimmedString(term)).filter((term) => term.length > 0);
    const remaining = [...terms];
    const rows: Array<QuotationEditorTermsRow> = DEFAULT_TERMS_AND_CONDITIONS.map((defaultTerm, index) => {
        const matchingIndex = remaining.findIndex((term) => term === defaultTerm);
        if (matchingIndex >= 0) {
            const [value] = remaining.splice(matchingIndex, 1);
            return {id: `default-term-${index}`, value: value ?? defaultTerm, isVisible: true, canDelete: false};
        }
        return {id: `default-term-${index}`, value: defaultTerm, isVisible: false, canDelete: false};
    });

    remaining.forEach((term, index) => {
        rows.push({id: `existing-term-${index}`, value: term, isVisible: true, canDelete: true});
    });

    return rows;
}

/**
 * Create behavior: defaults are always visible (even if not present).
 */
export function mapTermsRowsCreate(rawTerms: Array<string> | null | undefined): Array<QuotationEditorTermsRow> {
    const terms = (rawTerms ?? []).map((term) => asTrimmedString(term)).filter((term) => term.length > 0);
    const remaining = [...terms];
    const rows: Array<QuotationEditorTermsRow> = DEFAULT_TERMS_AND_CONDITIONS.map((defaultTerm, index) => {
        const matchingIndex = remaining.findIndex((term) => term === defaultTerm);
        if (matchingIndex >= 0) {
            const [value] = remaining.splice(matchingIndex, 1);
            return {id: `default-term-${index}`, value: value ?? defaultTerm, isVisible: true, canDelete: false};
        }
        return {id: `default-term-${index}`, value: defaultTerm, isVisible: true, canDelete: false};
    });

    remaining.forEach((term, index) => {
        rows.push({id: `existing-term-${index}`, value: term, isVisible: true, canDelete: true});
    });

    return rows;
}

export function toDMYDate(value: string): string {
    const parts = value.split("-");
    if (parts.length !== 3) {
        return value;
    }
    const [year, month, day] = parts;
    if (year.length === 4 && month.length === 2 && day.length === 2) {
        return `${day}/${month}/${year}`;
    }
    return value;
}

export function toYMDDate(value: string): string {
    const parts = value.split("/");
    if (parts.length !== 3) {
        return value;
    }
    const [day, month, year] = parts;
    if (day.length === 2 && month.length === 2 && year.length === 4) {
        return `${year}-${month}-${day}`;
    }
    return value;
}

export function SaveQuotationIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M18.1716 1C18.702 1 19.2107 1.21071 19.5858 1.58579L22.4142 4.41421C22.7893 4.78929 23 5.29799 23 5.82843V20C23 21.6569 21.6569 23 20 23H4C2.34315 23 1 21.6569 1 20V4C1 2.34315 2.34315 1 4 1H18.1716ZM4 3C3.44772 3 3 3.44772 3 4V20C3 20.5523 3.44772 21 4 21H5V15C5 13.3431 6.34315 12 8 12H16C17.6569 12 19 13.3431 19 15V21H20C20.5523 21 21 20.5523 21 20V6.82843C21 6.29799 20.7893 5.78929 20.4142 5.41421L18.5858 3.58579C18.2107 3.21071 17.702 3 17.1716 3H17V5C17 6.65685 15.6569 8 14 8H10C8.34315 8 7 6.65685 7 5V3H4ZM17 21V15C17 14.4477 16.5523 14 16 14H8C7.44772 14 7 14.4477 7 15V21H17ZM9 3H15V5C15 5.55228 14.5523 6 14 6H10C9.44772 6 9 5.55228 9 5V3Z"
                fill="currentColor"
            />
        </svg>
    );
}
